import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './ZoomFloat.module.css';

/**
 * ZoomFloat — ventana flotante con reunión de Zoom (reemplaza JitsiFloat).
 * Mismo comportamiento visual (arrastrar, minimizar, redimensionar, abrir en
 * pestaña nueva) — la diferencia real es que Zoom no tiene un SDK de React
 * que devuelva un iframe ya armado como @jitsi/react-sdk, así que el iframe
 * se arma a mano, apuntando a /meeting-frame/:assemblyId (misma app, otra
 * ruta) — esa página pide su propia firma al backend y arranca el Meeting
 * SDK ahí adentro. Ver INTEGRATION_GUIDE.md (zoom-meetingsdk-react-sample)
 * para todo el trasfondo de por qué se resolvió así el embebido.
 */
// A diferencia de JitsiMeeting (responsive, se reacomoda en cualquier
// tamaño), la UI del Meeting SDK de Zoom tiene una barra de controles de
// ancho fijo que no se adapta — con el tamaño chico que usaba JitsiFloat
// (480x380) queda cortada y aparecen scrollbars. Se necesita un tamaño
// inicial más grande para que se vea completa, pero sin tapar toda la
// pantalla — se limita al viewport disponible por si la ventana es chica.
const tamanoInicial = () => ({
  w: Math.min(900, window.innerWidth  - 40),
  h: Math.min(620, window.innerHeight - 100),
});

// Teléfonos (de pie o de lado). Ahí la reunión nace en la MITAD de la pantalla (abajo; de lado, a la derecha), se puede mover
// hacia arriba o abajo, pasar a pantalla completa y minimizar (ver ZoomFloat.module.css, `.movil`).
const CONSULTA_MOVIL = '(max-width: 600px), (max-height: 500px)';
const CONSULTA_HORIZONTAL = '(orientation: landscape)';
const esMovil = () => window.matchMedia(CONSULTA_MOVIL).matches;
const esHorizontal = () => window.matchMedia(CONSULTA_HORIZONTAL).matches;

const HEADER_H = 44;
const VISIBLE_MIN = 80;   // píxeles de la barra que siempre quedan a la vista al arrastrar

export default function ZoomFloat({ assemblyId, assemblyName, isModerator, onClose }) {
  const [pos,       setPos]       = useState(() => {
    const t = tamanoInicial();
    return {
      x: Math.max(20, window.innerWidth  - t.w - 40),
      y: Math.max(20, (window.innerHeight - t.h) / 2 - 40),
    };
  });
  const [size,      setSize]      = useState(tamanoInicial);
  // Minimizada: solo la barra. La reunión SIGUE conectada (el iframe no se desmonta): si se desmontara, minimizar sacaría a la persona de la llamada.
  const [minimized, setMinimized] = useState(false);
  const [movil,     setMovil]     = useState(esMovil);
  const [horizontal, setHorizontal] = useState(esHorizontal);
  const [completa,  setCompleta]  = useState(false);   // teléfono: pantalla completa (si no, la mitad)
  const [yMovil,    setYMovil]    = useState(null);    // teléfono, mitad: altura a la que la dejó la persona (null = pegada abajo)

  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const ventanaRef = useRef(null);

  const meetingFrameUrl = `${window.location.origin}/meeting-frame/${assemblyId}`;

  // El SDK, dentro del iframe, avisa por postMessage cuando el usuario sale
  // de la reunión (ver MeetingFrameLeft.jsx) — reaccionamos cerrando la
  // ventana flotante, igual que onReadyToClose en JitsiFloat.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'eventify-zoom-left') onClose?.();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onClose]);

  // Teléfono ↔ ventana flotante, y de pie ↔ de lado: se reacomoda al girar el aparato o cambiar el tamaño de la ventana.
  useEffect(() => {
    const mqMovil = window.matchMedia(CONSULTA_MOVIL);
    const mqHoriz = window.matchMedia(CONSULTA_HORIZONTAL);
    const onChange = () => { setMovil(mqMovil.matches); setHorizontal(mqHoriz.matches); setYMovil(null); };
    mqMovil.addEventListener('change', onChange);
    mqHoriz.addEventListener('change', onChange);
    return () => { mqMovil.removeEventListener('change', onChange); mqHoriz.removeEventListener('change', onChange); };
  }, []);

  // Si la ventana flotante queda fuera de la pantalla (tablet que gira, ventana del navegador que se achica), se mete de nuevo.
  useEffect(() => {
    if (movil) return undefined;
    const meter = () => {
      setSize(sz => {
        const w = Math.min(sz.w, Math.max(320, window.innerWidth)), h = Math.min(sz.h, Math.max(260, window.innerHeight));
        return (w === sz.w && h === sz.h) ? sz : { w, h };
      });
      setPos(ps => {
        const x = Math.max(0, Math.min(ps.x, window.innerWidth  - Math.min(size.w, Math.max(320, window.innerWidth))));
        const y = Math.max(0, Math.min(ps.y, window.innerHeight - HEADER_H));
        return (x === ps.x && y === ps.y) ? ps : { x, y };
      });
    };
    meter();
    window.addEventListener('resize', meter);
    return () => window.removeEventListener('resize', meter);
  }, [movil, size.w]);

  // ── Drag (puntero: mouse y dedo) ────────────────────────────────────────────
  // Teléfono, mitad de pantalla (de pie): solo sube y baja, siempre entera dentro de la pantalla.
  const movilMovible = movil && !completa && !minimized && !horizontal;
  const onPointerDown = useCallback((e) => {
    if (e.target.closest('.zoom-btn')) return;
    if (movil && !movilMovible) return;
    const alto = ventanaRef.current ? ventanaRef.current.getBoundingClientRect().height : size.h;
    const yInicial = movil ? (ventanaRef.current ? ventanaRef.current.getBoundingClientRect().top : 0) : pos.y;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: yInicial };
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* sin captura: sigue por la ventana */ }

    const onMove = (ev) => {
      if (!dragRef.current.dragging) return;
      const y = dragRef.current.origY + (ev.clientY - dragRef.current.startY);
      if (movil) { setYMovil(Math.max(0, Math.min(window.innerHeight - alto, y))); return; }
      // Siempre queda a la vista al menos un pedazo de la barra para poder volver a agarrarla (con el dedo es fácil perderla).
      setPos({
        x: Math.max(VISIBLE_MIN - size.w, Math.min(window.innerWidth - VISIBLE_MIN, dragRef.current.origX + (ev.clientX - dragRef.current.startX))),
        y: Math.max(0, Math.min(window.innerHeight - HEADER_H, y)),
      });
    };
    const onUp = () => {
      dragRef.current.dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [pos, movil, movilMovible, size.w, size.h]);

  const alternarCompleta = () => { setCompleta(c => !c); setYMovil(null); };

  const clases = [
    styles.ventana,
    movil && styles.movil,
    movil && completa && styles.completa,
    movil && horizontal && styles.horizontal,
    movil && yMovil !== null && styles.movida,
    minimized && styles.plegada,
  ].filter(Boolean).join(' ');

  return createPortal(
    <div
      ref={ventanaRef}
      className={clases}
      style={{
        '--x':        `${pos.x}px`,
        '--y':        `${pos.y}px`,
        '--w':        `${size.w}px`,
        '--h':        `${minimized ? HEADER_H : size.h}px`,
        '--min-h':    `${minimized ? HEADER_H : 260}px`,
        '--h-cuerpo': `${size.h - HEADER_H}px`,
        '--y-movil':  yMovil === null ? undefined : `${yMovil}px`,
      }}>

      {/* Header — drag handle */}
      <div
        onPointerDown={onPointerDown}
        className={styles.cabecera}>
        <i className={`fas fa-video ${styles.icono}`} />
        <span className={styles.titulo}>
          {assemblyName || 'Reunión virtual'}
        </span>

        {isModerator && (
          <span className={styles.moderador}>
            MODERADOR
          </span>
        )}

        <div className={styles.botones}>
          {/* Abrir en pestaña nueva */}
          <button className={`zoom-btn ${styles.boton}`}
            onClick={() => window.open(meetingFrameUrl, '_blank')}
            title="Abrir en pestaña nueva">
            <i className="fas fa-external-link-alt" />
          </button>
          {/* Teléfono: pantalla completa ↔ la mitad */}
          {movil && !minimized && (
            <button className={`zoom-btn ${styles.boton}`}
              onClick={alternarCompleta}
              title={completa ? 'Reducir a la mitad' : 'Pantalla completa'}>
              <i className={`fas ${completa ? 'fa-compress' : 'fa-expand'}`} />
            </button>
          )}
          {/* Minimizar */}
          <button className={`zoom-btn ${styles.boton}`}
            onClick={() => setMinimized(m => !m)}
            title={minimized ? (movil ? 'Restaurar' : 'Expandir') : 'Minimizar'}>
            <i className={`fas ${minimized ? (movil ? 'fa-window-restore' : 'fa-expand') : 'fa-minus'}`} />
          </button>
          {/* Cerrar */}
          <button className={`zoom-btn ${styles.botonCerrar}`}
            onClick={onClose}
            title="Cerrar reunión">
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {/* Zoom vía iframe — ver MeetingFrame.jsx, donde de verdad arranca el SDK. Siempre montado, también minimizada. */}
      <div className={styles.cuerpo}>
        <iframe
          src={meetingFrameUrl}
          title="Reunión de Zoom"
          allow="camera; microphone; fullscreen; display-capture"
          className={styles.marco}
        />
      </div>

      {/* Handle resize (oculto en teléfonos y minimizada) */}
      {!minimized && (
        <div
          onPointerDown={(e) => {
            e.preventDefault();
            // Con el puntero capturado, el redimensionado sigue aunque el mouse o el dedo pasen por encima del iframe de Zoom
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* sin captura: sigue por la ventana */ }
            const startW = size.w, startH = size.h, startX = e.clientX, startY = e.clientY;
            const onMove = (ev) => setSize({
              w: Math.max(320, startW + (ev.clientX - startX)),
              h: Math.max(260, startH + (ev.clientY - startY)),
            });
            const onUp = () => {
              window.removeEventListener('pointermove', onMove);
              window.removeEventListener('pointerup', onUp);
              window.removeEventListener('pointercancel', onUp);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
          }}
          className={styles.agarre}>
          <i className={`fas fa-grip-lines-vertical ${styles.agarreIcono}`} />
        </div>
      )}
    </div>,
    document.body
  );
}
